import { isTabletPortraitDown, ProviderUser, Spinner, Toast, BaseButton, isDemoModeEnabled, toggleDemoMode } from '@enter/core';
import { mdiArrowLeft } from '@mdi/js';
import { Icon } from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RootStore } from 'stores';
import { Company, Email, Name, Phone } from '.';
import { SubCategoryLink } from '../components';
import { switchForRoutes } from '../shared';
import * as S from '../shared.styles';

const OverviewComponent: React.FunctionComponent<{ rootStore?: RootStore }> = (props): React.ReactElement => {
            const { rootStore } = props;
            
            const [message, setMessage] = useState<string | undefined>(undefined);
            
            if (!rootStore) {
                return <></>;
            }
            
            // Direct observable access - this MUST trigger re-render when changed
            const users = rootStore.settingsStore.users;
            const roles = rootStore.settingsStore.roles;
            const userId = rootStore.userId;
            const hasAdminAccess = rootStore.roleId === 'PROVIDER_ADMIN_ACCESS';

            const usersLoaded = rootStore.settingsStore.usersLoaded;
            const rolesLoaded = rootStore.settingsStore.rolesLoaded;
            
            if (!usersLoaded || (hasAdminAccess && !rolesLoaded)) {
                return <Spinner />;
            }
            
            let currentUser = rootStore.settingsStore.getCurrentUser(userId);

            //This is backup in case the current user is not found in the users list
            if(!currentUser) {
                currentUser = rootStore.getProviderUserFromAccount();
            }

            if (!currentUser && !isDemoModeEnabled()) {
                return <Spinner />;
            }

            const name = `${currentUser?.first || ''} ${currentUser?.last || ''}`;
            const email = currentUser?.email || '';
            const phone = currentUser?.phone || '';
            const company = rootStore.companyName || '';
            const shouldShowDemoMode = isDemoModeEnabled() || email.endsWith('@enter.health') || email.endsWith('@teammwf.com');
            
            const handleMessageClose = (): void => {
                setMessage(undefined);
            };

            const renderMobile = (): React.ReactElement => (
                <Routes>
                    <Route path="/" element={renderLeftColumn()} />
                    <Route path="name" element={<Name />} />
                    <Route path="email" element={<Email />} />
                    <Route path="phone" element={<Phone />} />
                    <Route path="company" element={<Company />} />
                </Routes>
            );

            const renderLeftColumn = (): React.ReactElement => (
                <S.SubCategoryCol>
                    <S.SubCategoryHeader>
                        {isTabletPortraitDown() && (
                            <S.ReturnLink to="/settings">
                                {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                            </S.ReturnLink>
                        )}
                        <S.SubCategoryH1>Overview</S.SubCategoryH1>
                    </S.SubCategoryHeader>
                    <SubCategoryLink
                        first={true}
                        to="/settings/overview/name"
                        label="Name"
                        value={name}
                    />
                    <SubCategoryLink to="/settings/overview/email" label="Email" value={email} />
                    <SubCategoryLink to="/settings/overview/phone" label="Phone" value={phone} />
                    <SubCategoryLink to="/settings/overview/company" label="Company" value={company} />
                    {shouldShowDemoMode && (
                        <S.DemoModeSection>
                            <S.DemoModeHeader>Demo Mode</S.DemoModeHeader>
                            <S.DemoModeStatus>
                                Status: <strong>{isDemoModeEnabled() ? 'Enabled' : 'Disabled'}</strong>
                            </S.DemoModeStatus>
                            <BaseButton 
                                type="button" 
                                variant={isDemoModeEnabled() ? 'warning' : 'primary'}
                                onClick={toggleDemoMode}
                            >
                                {isDemoModeEnabled() ? 'Disable Demo Mode' : 'Enable Demo Mode'}
                            </BaseButton>
                            <S.DemoModeNote>
                                {isDemoModeEnabled() 
                                    ? 'Demo mode routes API calls to the demo environment' 
                                    : 'Enable to route API calls to the demo environment'}
                            </S.DemoModeNote>
                        </S.DemoModeSection>
                    )}
                    <Toast message={message as string} onClose={handleMessageClose} />
                </S.SubCategoryCol>
            );

            const renderRightColumn = (): React.ReactElement => (
                <S.SubCategoryCol>
                    {switchForRoutes([
                        { path: 'name', Component: Name },
                        { path: 'email', Component: Email },
                        { path: 'phone', Component: Phone },
                        { path: 'company', Component: Company },
                    ])}
                </S.SubCategoryCol>
            );
            
            return (
                <S.SubCategoryRow>
                    {isTabletPortraitDown() && renderMobile()}
                    {!isTabletPortraitDown() && renderLeftColumn()}
                    {!isTabletPortraitDown() && renderRightColumn()}
                </S.SubCategoryRow>
            );
};

export const Overview = inject('rootStore')(observer(OverviewComponent));
